import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { vratPoradieZostav } from "@/api/hooks/useZostavy";
import type { CelokZmena, DataResponse } from "@/api/schemas";
import { useUndoOptional } from "@/lib/undo/UndoContext";

/**
 * Celky zostáv — Kuchyňa, Obývačka nad zostavami (KOLO 235).
 *
 * Každá zmena je jeden krok späť: odpoveď nesie poradie zostáv a ich celky
 * pred zmenou (`predtym`, `predtym_celky`), tie sa pošlú na
 * `zostavy/poradie`; zrušený celok sa najprv obnoví, nový zmaže.
 */

function obnov(qc: ReturnType<typeof useQueryClient>, zakazkaId: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
}

export function useVytvorCelok(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<
  DataResponse<CelokZmena>,
  Error,
  { nazov: string; zostava_ids?: string[] }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post<DataResponse<CelokZmena>>(
        `/varianty/${variantaId}/celky`,
        { nazov: payload.nazov, zostava_ids: payload.zostava_ids ?? [] },
      );
      return data;
    },
    onSuccess: async ({ data }) => {
      const celokId = data.celok?.id;
      undo?.pridaj({
        popis: `nový celok ${data.celok?.nazov ?? ""}`.trim(),
        vrat: async () => {
          if (celokId) await api.delete(`/celky/${celokId}`);
          if ((data.predtym ?? []).length > 0) {
            await vratPoradieZostav(variantaId, data.predtym ?? [], data.predtym_celky);
          }
          await obnov(qc, zakazkaId);
        },
      });
      await obnov(qc, zakazkaId);
    },
  });
}

export function usePremenujCelok(
  zakazkaId: string,
): UseMutationResult<DataResponse<CelokZmena>, Error, { celokId: string; nazov: string }> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async ({ celokId, nazov }) => {
      const { data } = await api.patch<DataResponse<CelokZmena>>(`/celky/${celokId}`, {
        nazov,
      });
      return data;
    },
    onSuccess: async ({ data }, { celokId, nazov }) => {
      const stary = data.predtym_nazov;
      if (stary && stary !== nazov) {
        undo?.pridaj({
          popis: `premenovanie celku ${stary}`,
          vrat: async () => {
            await api.patch(`/celky/${celokId}`, { nazov: stary });
            await obnov(qc, zakazkaId);
          },
        });
      }
      await obnov(qc, zakazkaId);
    },
  });
}

export function useZrusCelok(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<DataResponse<CelokZmena>, Error, { celokId: string; nazov: string }> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async ({ celokId }) => {
      const { data } = await api.delete<DataResponse<CelokZmena>>(`/celky/${celokId}`);
      return data;
    },
    onSuccess: async ({ data }, { celokId, nazov }) => {
      undo?.pridaj({
        popis: `zrušenie celku ${nazov}`,
        vrat: async () => {
          await api.post(`/celky/${celokId}/obnovit`);
          await vratPoradieZostav(variantaId, data.predtym ?? [], data.predtym_celky);
          await obnov(qc, zakazkaId);
        },
      });
      await obnov(qc, zakazkaId);
    },
  });
}

export function usePoradieCelkov(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<DataResponse<CelokZmena>, Error, { celky: string[]; popis?: string }> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async ({ celky }) => {
      const { data } = await api.post<DataResponse<CelokZmena>>(
        `/varianty/${variantaId}/celky/poradie`,
        { celky },
      );
      return data;
    },
    onSuccess: async ({ data }, { popis }) => {
      const predtym = data.predtym_celky_poradie ?? [];
      undo?.pridaj({
        popis: popis ?? "posunutie celku",
        vrat: async () => {
          await api.post(`/varianty/${variantaId}/celky/poradie`, { celky: predtym });
          await obnov(qc, zakazkaId);
        },
      });
      await obnov(qc, zakazkaId);
    },
  });
}

/** Dať do celku — zostavy na koniec celku, `celokId: null` = bez celku. */
export function useZaradDoCelku(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<
  DataResponse<CelokZmena>,
  Error,
  { zostavaIds: string[]; celokId: string | null; popis?: string }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();
  return useMutation({
    mutationFn: async ({ zostavaIds, celokId }) => {
      const { data } = await api.post<DataResponse<CelokZmena>>(
        `/varianty/${variantaId}/zostavy/celok`,
        { zostava_ids: zostavaIds, celok_id: celokId },
      );
      return data;
    },
    onSuccess: async ({ data }, { popis }) => {
      if (Object.keys(data.predtym_celky ?? {}).length > 0) {
        undo?.pridaj({
          popis: popis ?? "presun do celku",
          vrat: async () => {
            await vratPoradieZostav(variantaId, data.predtym ?? [], data.predtym_celky);
            await obnov(qc, zakazkaId);
          },
        });
      }
      await obnov(qc, zakazkaId);
    },
  });
}
